import * as React from "react";
import {
  IconButton,
  Dialog,
  DialogContent,
  Button,
  Box,
  Slide,
  DialogTitle,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { merge } from "lodash";
import { useModalScrollBlocker } from "src/utils/useModalScrollBlocker";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Track the iOS keyboard via visualViewport so bottom-anchored dialogs sit
// just above the keyboard instead of behind it. Without this, the Paper
// anchors to the layout-viewport bottom (below the keyboard) until the
// user starts typing, at which point Safari belatedly scrolls the input
// into view — producing the "tray covered for a beat, then jumps up"
// flicker on first focus.
function useKeyboardOffset(open) {
  const [offset, setOffset] = React.useState(0);
  React.useEffect(() => {
    if (!open) {
      setOffset(0);
      return undefined;
    }
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      const keyboardHeight = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop
      );
      setOffset(keyboardHeight);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [open]);
  return offset;
}

const BottomDialog = ({
  open,
  handleClose,
  children,
  sx = {},
  dialogTitle,
  dividers,
  hideCloseButton = false,
  hideRectangle = false,
  bubbleStyle = false,
  scroll = "paper",
  ...props
}) => {
  const Rectangle = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: "8px",
          bottom: "8px",
          left: "50%",
          right: "50%",
          width: "32px",
          height: "4px",
          backgroundColor: "#79747E",
          opacity: 0.4,
          borderRadius: "100px",
          transform: "translateX(-50%)",
          zIndex: 0,
        }}
      />
    );
  };

  const CloseButton = () => {
    return (
      <IconButton
        edge="end"
        color="inherit"
        variant="outlined"
        data-testid="dialog-close-button"
        onClick={handleClose}
        aria-label="close"
        sx={{ position: "absolute", top: "22px", right: "26px", zIndex: 30 }}
      >
        <Close fontSize="small" />
      </IconButton>
    );
  };

  const bubbleSx = {
    height: "auto",
    ".MuiPaper-root": {
      borderRadius: "16px 16px 0px 0px",
    },
    ".MuiDialogTitle-root": {
      padding: hideCloseButton ? 1.5 : "32px 12px",
    },
  };

  useModalScrollBlocker({ open, enabled: !props.disableScrollLock });
  const keyboardOffset = useKeyboardOffset(open);
  return (
    <Dialog
      fullWidth
      scroll={scroll}
      container={document.getElementById("portal-root")}
      disableScrollLock={true}
      sx={merge(
        {
          ".MuiDialog-container": {
            alignItems: "flex-end",
            // Push the tray up by the keyboard height so the Paper anchors
            // to the visible-viewport bottom, not the layout-viewport bottom.
            paddingBottom: keyboardOffset ? `${keyboardOffset}px` : 0,
            transition: "padding-bottom 150ms ease-out",
            "@supports (height: 100dvh)": { minHeight: "100dvh" },
            "@supports not (height: 100dvh)": {
              minHeight: "-webkit-fill-available",
            },
          },
          ".MuiPaper-root": {
            width: "100%",
            margin: 0,
            borderRadius: "4px 4px 0px 0px",
            "@supports (height: 100dvh)": { maxHeight: "100dvh" },
            "@supports not (height: 100dvh)": {
              maxHeight: "-webkit-fill-available",
            },
          },
        },
        bubbleStyle ? bubbleSx : {},
        sx
      )}
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      {...props}
      BackdropProps={merge(
        {
          sx: {
            "@supports (height: 100dvh)": { height: "100dvh" },
            "@supports not (height: 100dvh)": {
              height: "-webkit-fill-available",
            },
          },
        },
        props.BackdropProps ?? {}
      )}
    >
      <DialogTitle
        sx={{
          mt: hideCloseButton ? "0rem !important" : "0.75rem !important",
          p: 2,
        }}
        variant="h2"
      >
        {dialogTitle}
      </DialogTitle>
      {!hideCloseButton && <CloseButton />}
      <DialogContent
        dividers={dividers}
        sx={{
          overflowY: "auto",
          "@supports (height: 100dvh)": { maxHeight: "calc(100dvh - 64px)" },
          "@supports not (height: 100dvh)": {
            maxHeight: "calc(-webkit-fill-available - 64px)",
          },
        }}
      >
        {!hideRectangle && <Rectangle />}
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default BottomDialog;
